import { forwardRef, useCallback, useEffect } from "react";
import clsx from "clsx";
import {
  Sparkles,
  Bold,
  Italic,
  Pilcrow,
  Heading1,
  Heading2,
  Heading3,
  List,
  ListOrdered,
  Link as LinkIcon,
} from "lucide-react";
import TextStyle from "@tiptap/extension-text-style";
import { useEditor, EditorContent } from "@tiptap/react";
import StarterKit from "@tiptap/starter-kit";
import Placeholder from "@tiptap/extension-placeholder";
import Link from "@tiptap/extension-link";
import "./index.css";

const toolbarButtonClasses = [
  "px-3 h-10 text-default-400",
  "hover:text-default-600 hover:bg-white",
];
const activeButtonClasses = "!text-default-600 bg-white";

const MenuBar = ({ editor, openAiDrawer }) => {
  if (!editor) return null;

  const setLink = useCallback(() => {
    const previousUrl = editor.getAttributes("link").href;

    // If already a link, unlink it
    if (previousUrl) {
      editor.chain().focus().unsetLink().run();
      return;
    }

    const url = window.prompt("URL du lien :");
    if (url === null) return; // cancelled
    if (url === "") {
      editor.chain().focus().extendMarkRange("link").unsetLink().run();
      return;
    }

    editor
      .chain()
      .focus()
      .extendMarkRange("link")
      .setLink({ href: url })
      .run();
  }, [editor]);

  return (
    <div
      className={clsx(
        "overflow-hidden flex space-x-2 items-center justify-between sticky rounded-lg",
        "ring-1 ring-default-200 bg-default-50 shadow-md shadow-default-100",
      )}
    >
      <div className="flex items-center divide-x divide-default-200">
        {[
          { cmd: "toggleBold", icon: Bold, type: "bold" },
          { cmd: "toggleItalic", icon: Italic, type: "italic" },
          { cmd: "setParagraph", icon: Pilcrow, type: "paragraph" },
          { cmd: "toggleHeading", icon: Heading1, type: "heading", level: 1 },
          { cmd: "toggleHeading", icon: Heading2, type: "heading", level: 2 },
          { cmd: "toggleHeading", icon: Heading3, type: "heading", level: 3 },
          { cmd: "toggleBulletList", icon: List, type: "bulletList" },
          { cmd: "toggleOrderedList", icon: ListOrdered, type: "orderedList" },
        ].map(({ cmd, icon: Icon, type, level }) => (
          <button
            key={type + (level || "")}
            type="button"
            onClick={() => editor.chain().focus()[cmd]({ level }).run()}
            disabled={!editor.can().chain().focus()[cmd]({ level }).run()}
            className={clsx(toolbarButtonClasses, {
              [activeButtonClasses]: editor.isActive(type, { level }),
            })}
          >
            <Icon className="w-4 h-4" />
          </button>
        ))}
        <button
          key="link"
          type="button"
          onClick={setLink}
          className={clsx(toolbarButtonClasses, {
            [activeButtonClasses]: editor.isActive("link"),
          })}
        >
          <LinkIcon className="w-4 h-4" />
        </button>
      </div>
      <button
        className={clsx(
          "h-10 w-10 border-l border-default-300 text-default-600",
          "flex items-center justify-center hover:bg-default-50/50 bg-white",
        )}
        type="button"
        onClick={openAiDrawer}
      >
        <Sparkles className="w-5 h-5" />
      </button>
    </div>
  );
};

const contentClasses = [
  "[&_h1]:font-semibold",
  "[&_h2]:font-semibold",
  "[&_h3]:font-semibold",
  "[&_h4]:font-semibold",
  "[&_h1]:!text-default-600",
  "[&_h2]:!text-default-600",
  "[&_h3]:!text-default-600",
  "[&_h4]:!text-default-600",
  "[&_h1]:text-3xl",
  "[&_h1]:mb-6",
  "[&_h2]:text-2xl",
  "[&_h2]:mb-4",
  "[&_h3]:text-xl",
  "[&_h3]:mb-2",
  "[&_h4]:text-lg",
  "[&_h4]:mb-2",
  "[&_p]:mb-2",
  "[&_strong]:font-semibold",
  "[&_ul]:list-disc",
  "[&_ul]:ml-6",
  "[&_ul]:mb-4",
  "[&_ol]:list-decimal",
  "[&_ol]:ml-6",
  "[&_ol]:mb-4",
  "[&_li]:mb-1",
  "[&_a]:text-default-600",
  "[&_a]:underline",
  "[&_a]:cursor-pointer",
];

const TextArea = forwardRef(
  (
    {
      className,
      name,
      value,
      onChange = () => {},
      openAiDrawer = () => {},
      showMenubar = true,
      placeholder,
      minRows = 4,
      rows = 4,
      autofocus = false,
    },
    ref,
  ) => {
    const extensions = [
      TextStyle.configure(),
      StarterKit.configure(),
      Placeholder.configure({ placeholder }),
      Link.configure({
        openOnClick: false,
        autolink: true,
        HTMLAttributes: {
          class: "text-default-600 underline cursor-pointer",
          rel: "noopener noreferrer",
          target: "_blank",
        },
      }),
    ];

    const editor = useEditor({
      extensions,
      content: value,
      onUpdate: ({ editor }) => onChange(editor.getHTML()),
      autofocus,
    });

    useEffect(() => {
      if (editor && value !== editor.getHTML()) {
        editor.commands.setContent(value);
      }
    }, [value, editor]);

    const safeRows = Number(rows) > 0 ? Number(rows) : 4;
    const safeminRows = Number(minRows) > 0 ? Number(minRows) : 4;

    const minHeight = safeRows > 0 ? `${safeRows * 24 + 32}px` : undefined;

    const handleContainerClick = (e: React.MouseEvent<HTMLDivElement>) => {
      if (editor && !editor.isFocused) {
        // Focus the editor and move cursor to the end
        editor.chain().focus().command(({ tr, commands }) => {
          // Move cursor to the end of the document
          const docSize = tr.doc.content.size;
          return commands.setTextSelection(docSize);
        }).run();
      }
    };

    return (
      <div className="relative w-full">
        <div className="flex flex-col space-y-4 w-full">
          {showMenubar && editor && (
            <MenuBar editor={editor} openAiDrawer={openAiDrawer} />
          )}
          <div
            onClick={handleContainerClick}
            className="cursor-text w-full"
            style={minHeight ? { minHeight } : undefined}
          >
            <EditorContent
              editor={editor}
              className={clsx(
                className,
                contentClasses,
                "[&_*]:focus:!outline-none",
              )}
              style={minHeight ? { minHeight } : undefined}
            />
          </div>
        </div>
        <input type="hidden" name={name} value={value} />
      </div>
    );
  },
);

export default TextArea;
